import React, { useState, useEffect } from 'react';
import { Download, Share2, Printer } from 'lucide-react';
import { toast } from 'sonner';

const Draft = () => {
  // Countdown state
  const [countdown, setCountdown] = useState({
    days: 1,
    hours: 21,
    mins: 28,
    secs: 58
  });

  // Draft board columns (team owners)
  const columns = ['Wade', 'FFLNX', 'CpatrickNFL', 'DavisMattek', 'JustinHerzig', 'Valkyria', 'ship'];

  // Sample player names
  const playerNames = [
    'Jerome Bell', 'Guy Hawkins', 'Savannah Nguyen', 'Devon Lane', 'Theresa Webb', 'Kristin Watson',
    'Marvin McKinney', 'Jenny Wilson', 'Darlene Robertson', 'Albert Flores', 'Courtney Henry',
    'Cameron Williamson', 'Kathryn Murphy', 'Brooklyn Simmons', 'Jane Cooper', 'Floyd Miles',
    'Ronald Richards', 'Dianne Russell', 'Esther Howard', 'Wade Warren', 'Leslie Alexander'
  ];

  // Generate draft board data
  const generateDraftBoard = () => {
    const board = [];
    for (let round = 1; round <= 12; round++) {
      const row = [];
      for (let col = 0; col < columns.length; col++) {
        const playerIndex = (round * columns.length + col) % playerNames.length;
        const isGreen = Math.random() > 0.6;
        const isOrange = !isGreen && Math.random() > 0.7;
        const isPurple = !isGreen && !isOrange && Math.random() > 0.8;
        row.push({
          name: playerNames[playerIndex],
          color: isGreen ? 'bg-green-600' : isOrange ? 'bg-orange-500' : isPurple ? 'bg-purple-600' : 'bg-gray-700'
        });
      }
      board.push(row);
    }
    return board;
  };

  const [draftBoard] = useState(generateDraftBoard());

  // My players
  const myPlayers = [
    { initials: 'MU', name: 'Clarence Wallace', number: 43, position: 'Defender', bgColor: 'bg-red-600' },
    { initials: 'RM', name: 'Alexander Coleman', number: 12, position: 'Defender', bgColor: 'bg-blue-500' },
  ];

  // Recent picks
  const recentPicks = [
    { initials: 'LM', name: 'Edwin Klein', number: 10, position: 'Defender', bgColor: 'bg-red-500', picker: 'Dianne Player', time: '21:00 06 Nov 2021' },
    { initials: 'OP', name: 'Dennis Copeland', number: 12, position: 'Defender', bgColor: 'bg-green-500', picker: 'Jerome Player', time: '13:00 12 Nov 2021' },
    { initials: 'PE', name: 'Chase Evans', number: 11, position: 'Defender', bgColor: 'bg-purple-500', picker: 'Tian Player', time: '21:00 06 Nov 2021' },
    { initials: 'MO', name: 'Ralph Warner', number: '01', position: 'Defender', bgColor: 'bg-yellow-500', picker: 'Darrel Player', time: '13:00 12 Nov 2021' },
  ];

  // Countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        let { days, hours, mins, secs } = prev;
        secs--;
        if (secs < 0) { secs = 59; mins--; }
        if (mins < 0) { mins = 59; hours--; }
        if (hours < 0) { hours = 23; days--; }
        if (days < 0) { days = 0; hours = 0; mins = 0; secs = 0; }
        return { days, hours, mins, secs };
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (num) => num.toString().padStart(2, '0');

  return (
    <div className="min-h-screen bg-black p-4 md:p-6">
      <div className="max-w-[1800px] mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">Next Game</h1>
            <p className="text-gray-400 mt-1">Manchester United F.C. VS Real Madrid CF</p>
          </div>
          
          <div className="flex items-center gap-3">
            <button 
              onClick={() => toast.info('Exporting draft data...')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <Download size={18} />
              Export
            </button>
            <button 
              onClick={() => toast.info('Share link copied!')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <Share2 size={18} />
              Share
            </button>
            <button 
              onClick={() => toast.info('Opening print dialog...')}
              className="flex items-center gap-2 px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              <Printer size={18} />
              Print
            </button>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Left Sidebar - Countdown & My Players */}
          <div className="w-72 flex-shrink-0 space-y-4">
            {/* Countdown Timer */}
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-6">
              <div className="flex items-center justify-center gap-2 text-white">
                <div className="text-center">
                  <span className="text-5xl font-bold">{formatTime(countdown.days)}</span>
                  <p className="text-gray-400 text-sm mt-1">Days</p>
                </div>
                <span className="text-5xl font-bold">:</span>
                <div className="text-center">
                  <span className="text-5xl font-bold">{formatTime(countdown.hours)}</span>
                  <p className="text-gray-400 text-sm mt-1">Hours</p>
                </div>
                <span className="text-5xl font-bold">:</span>
                <div className="text-center">
                  <span className="text-5xl font-bold">{formatTime(countdown.mins)}</span>
                  <p className="text-gray-400 text-sm mt-1">Mins</p>
                </div>
                <span className="text-5xl font-bold">:</span>
                <div className="text-center">
                  <span className="text-5xl font-bold">{formatTime(countdown.secs)}</span>
                  <p className="text-gray-400 text-sm mt-1">Secs</p>
                </div>
              </div>
            </div>

            {/* My Players */}
            {myPlayers.map((player, idx) => (
              <div key={idx} className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 ${player.bgColor} rounded-full flex items-center justify-center text-white font-bold text-sm`}>
                    {player.initials}
                  </div>
                  <span className="text-gray-400 text-sm">My Player</span>
                </div>
                <h3 className="text-white text-xl font-bold">{player.name}</h3>
                <p className="text-gray-400">{player.number} {player.position}</p>
              </div>
            ))}
          </div>

          {/* Main Content - Draft Board */}
          <div className="flex-1 overflow-x-auto">
            <div className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="px-2 py-3 text-gray-400 text-sm font-normal"></th>
                    {columns.map((col, idx) => (
                      <th key={idx} className="px-2 py-3 text-gray-400 text-sm font-normal text-center">
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {draftBoard.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td className="px-3 py-2 text-gray-400 text-center">{rowIdx + 1}</td>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx} className="px-1 py-1">
                          <div 
                            className={`${cell.color} px-3 py-2 rounded text-white text-sm text-center truncate cursor-pointer hover:opacity-80 transition-opacity`}
                            onClick={() => toast.info(`Selected: ${cell.name}`)}
                          >
                            {cell.name.length > 12 ? cell.name.substring(0, 12) + '...' : cell.name}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Right Sidebar - Recent Picks */}
          <div className="w-72 flex-shrink-0 space-y-4">
            {recentPicks.map((pick, idx) => (
              <div key={idx} className="bg-[#1a1a2e] border border-gray-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 ${pick.bgColor} rounded-full flex items-center justify-center text-white font-bold text-xs`}>
                      {pick.initials}
                    </div>
                    <span className="text-gray-400 text-sm">{pick.picker}</span>
                  </div>
                  <span className="text-gray-500 text-xs">{pick.time}</span>
                </div>
                <h3 className="text-white font-bold">{pick.name}</h3>
                <p className="text-gray-400 text-sm">{pick.number} {pick.position}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Draft;
